// client/src/DataContext.js

import React, { createContext, useState, useContext, useEffect } from 'react';
import { getDocs, collection, doc, getDoc, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { firestore } from './firebase'; // Assure-toi que tu as bien exporté firestore depuis ton fichier firebase.js

// Version de cache pour invalidation automatique en cas de breaking change
const APP_CACHE_VERSION = '2'; // incrémenter à chaque breaking change

// Invalidation du cache si la version stockée ne correspond pas
try {
  const storedVersion = localStorage.getItem('dataCacheVersion');
  if (storedVersion !== APP_CACHE_VERSION) {
    localStorage.removeItem('dataCache');
    localStorage.setItem('dataCacheVersion', APP_CACHE_VERSION);
  }
} catch (e) {
  console.warn('Impossible de vérifier la version du cache', e);
  // Dans le pire des cas, on purge tout
  localStorage.removeItem('dataCache');
  localStorage.setItem('dataCacheVersion', APP_CACHE_VERSION);
}

const DataContext = createContext();

// Récupération des données depuis Firestore (scopée par utilisateur si besoin)
const getDataFromFirebase = async (page, uid) => {
  // Helper commun pour limiter la portée d’un fetch à l’utilisateur courant
  const fetchScoped = async ({ col, fields = [] }) => {
    const results = [];
    const seen = new Set();
    for (const field of fields) {
      try {
        const snap = await getDocs(query(col, where(field, '==', uid)));
        snap.forEach((d) => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          results.push({ id: d.id, ...d.data() });
        });
      } catch {
        // ignore pour continuer les autres champs
      }
    }
    return results;
  };

  if (page === 'clientInvoices') {
    if (!uid) throw new Error('User not authenticated');
    const col = collection(firestore, 'clientInvoices');
    let rows = await fetchScoped({ col, fields: ['uid', 'userId', 'ownerUid'] });
    if (rows.length === 0) {
      try {
        const snap = await getDoc(doc(firestore, 'clientInvoices', uid));
        if (snap.exists()) rows = [{ id: snap.id, ...snap.data() }];
      } catch {
        // ignore
      }
    }
    return rows;
  }

  if (page === 'merchantInvoices') {
    if (!uid) throw new Error('User not authenticated');
    const col = collection(firestore, 'merchantInvoices');
    let rows = await fetchScoped({ col, fields: ['merchantId', 'merchantUid', 'ownerUid', 'uid', 'userId'] });
    if (rows.length === 0) {
      try {
        const snap = await getDoc(doc(firestore, 'merchantInvoices', uid));
        if (snap.exists()) rows = [{ id: snap.id, ...snap.data() }];
      } catch {
        // ignore
      }
    }
    return rows;
  }

  const snapshot = await getDocs(collection(firestore, page));
  const data = snapshot.docs.map(docu => docu.data());
  console.log('Data from Firebase:', data);
  return data;
};

export const DataProvider = ({ children }) => {
  // Chargement initial du cache, avec try/catch pour JSON.parse
  const [dataCache, setDataCache] = useState(() => {
    try {
      const raw = localStorage.getItem('dataCache');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.warn('Cache corrompu, purge automatique', e);
      localStorage.removeItem('dataCache');
      return {};
    }
  });

  const fetchData = async (page) => {
    const uid = getAuth().currentUser?.uid || null;
    const cacheKey = uid ? `${page}::${uid}` : page;
    console.log('Current dataCache:', dataCache);

    // Si on a déjà ce page pour cet utilisateur dans le cache, on renvoie immédiatement
    if (dataCache[cacheKey]) {
      console.log(`Returning cached data for ${cacheKey}`);
      return dataCache[cacheKey];
    }

    // Sinon, on va chercher sur Firebase
    const data = await getDataFromFirebase(page, uid);
    console.log('Fetched data:', data);

    // Mise à jour du state et du localStorage
    const updatedCache = { ...dataCache, [cacheKey]: data };
    setDataCache(updatedCache);
    try {
      localStorage.setItem('dataCache', JSON.stringify(updatedCache));
    } catch (e) {
      console.error('Échec de la sauvegarde du cache', e);
    }

    return data;
  };

  // Synchronisation du cache à chaque modification
  useEffect(() => {
    try {
      localStorage.setItem('dataCache', JSON.stringify(dataCache));
    } catch (e) {
      console.error('Échec de la sauvegarde du cache en useEffect', e);
    }
  }, [dataCache]);

  return (
    <DataContext.Provider value={{ fetchData, dataCache }}>
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);
